import React, { useEffect, useState } from 'react';
import { useManagerStore } from '../../../lib/store-manager';

interface PerformanceSummaryProps {
    userId: string;
}

export const PerformanceSummary: React.FC<PerformanceSummaryProps> = ({ userId }) => {
    const { userSummary, isGeneratingSummary, generateUserSummary, fetchUserSummary } = useManagerStore();
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        fetchUserSummary(userId);
    }, [userId, fetchUserSummary]);

    // Initial check
    useEffect(() => {
        const timer = setTimeout(() => {
            if (userSummary === null && !isGeneratingSummary) {
                generateUserSummary(userId);
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, [userSummary, userId, generateUserSummary, isGeneratingSummary]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await generateUserSummary(userId);
        setIsRefreshing(false);
    };

    if (isGeneratingSummary || isRefreshing) {
        return (
            <div className="performance-summary loading-shimmer" style={{ padding: '24px', background: '#f9fafb', borderRadius: '12px', minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
                <div className="spinner" style={{ width: '32px', height: '32px', border: '3px solid #e5e7eb', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>
                    Geçmiş konuşmalar taranıyor ve analiz ediliyor...
                </span>
                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (!userSummary) {
        return (
            <div className="performance-summary" style={{ padding: '24px', background: '#f9fafb', borderRadius: '12px', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Henüz analiz verisi yok.</span>
                <button onClick={handleRefresh} style={{ padding: '8px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Analizi Başlat</button>
            </div>
        );
    }

    const data = userSummary;

    return (
        <div className="performance-summary" style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>
                        Performans & Duygu Analizi
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
                        Son {data.last_simulation_count || '20'} simülasyon baz alınarak oluşturulmuştur.
                    </p>
                </div>
                <button
                    onClick={handleRefresh}
                    title="Yeniden Analiz Et"
                    style={{
                        background: 'white', border: '1px solid #d1d5db', cursor: 'pointer', color: '#374151',
                        display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500
                    }}
                >
                    <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>refresh</span>
                    Yenile
                </button>
            </div>

            {/* 1. EMOTIONAL ANALYSIS (TEXT ONLY) */}
            <div className="emotional-section" style={{ background: '#f0fdfa', borderRadius: '12px', padding: '20px', borderLeft: '4px solid #0d9488' }}>
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#0f766e', marginTop: 0, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined">psychology</span>
                    Duygu Durum Analizi
                </h4>
                <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#334155', margin: 0, whiteSpace: 'pre-line' }}>
                    {data.emotional_analysis || "Duygu analizi verisi mevcut değil."}
                </p>
            </div>

            {/* 2. OVERVIEW (Personalized) */}
            <div className="overview-section">
                <h4 style={{ fontSize: '15px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Genel Değerlendirme</h4>
                <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#374151', background: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    {data.overview}
                </p>
            </div>

            {/* 3. POINTS GRID */}
            <div className="analysis-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Positives */}
                <div className="positives" style={{ background: '#f0fdf4', padding: '16px', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#15803d', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="material-symbols-outlined">thumb_up</span>
                        Başarılı Noktalar (Kanıtlı)
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#166534', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {data.positives?.map((item: string, i: number) => (
                            <li key={i} style={{ lineHeight: '1.5' }}>{item}</li>
                        )) || <li>Veri yok</li>}
                    </ul>
                </div>

                {/* Negatives */}
                <div className="negatives" style={{ background: '#fef2f2', padding: '16px', borderRadius: '12px', border: '1px solid #fecaca' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#b91c1c', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className="material-symbols-outlined">thumb_down</span>
                        Gelişim Alanları (Kanıtlı)
                    </h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#991b1b', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {data.negatives?.map((item: string, i: number) => (
                            <li key={i} style={{ lineHeight: '1.5' }}>{item}</li>
                        )) || <li>Veri yok</li>}
                    </ul>
                </div>
            </div>

            {/* 4. ACTIONS */}
            <div className="improvements">
                <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#d97706', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="material-symbols-outlined">lightbulb</span>
                    Aksiyon Planı
                </h4>
                <div style={{ background: '#fffbeb', padding: '16px', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#92400e', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {data.improvements?.map((item: string, i: number) => (
                            <li key={i} style={{ lineHeight: '1.5' }}>{item}</li>
                        )) || <li>Veri yok</li>}
                    </ul>
                </div>
            </div>
        </div>
    );
};
